import { ENTITY_TYPE_INDIVIDUAL } from '../shared/useOnboardingState';
import { formatEIN } from './orgSetupUtils';
import { readPlanIntent } from '../shared/planIntent';

const DEFAULT_BRAND_COLOR = '#69995E';

export function buildIndividualEntityPatch() {
  return {
    entityType: ENTITY_TYPE_INDIVIDUAL,
    legalName: '',
    ein: '',
    city: '',
    state: '',
    nteeCode: '',
    causeCategory: '',
    einLookupStatus: 'idle',
    einLookupPayload: null,
    isTaxExempt: null,
    annualRevenue: '',
    hasWebsite: false,
    websiteUrl: '',
    websiteEnrichStatus: 'idle',
    description: '',
    logoUrl: '',
    ogImage: '',
    ogDescription: '',
    favicon: '',
    suggestedBrandColor: '',
    brandColor: '',
    themeColor: '',
    userUploadedLogoUrl: '',
  };
}

export function getCreateOrgLogoSource(state) {
  return state.logoUrl || state.ogImage || '';
}

export function getCreateOrgDisplayName(state) {
  const orgName = state.orgName?.trim() || '';
  if (state.entityType === ENTITY_TYPE_INDIVIDUAL) {
    return orgName;
  }
  return orgName || state.legalName?.trim() || '';
}

// PE-2048: URL-name (slug) suggestion from the org name — mirrors the backend
// rules in accounts-backend functions/org_slug_utils.py (lowercase alnum +
// single hyphens, max 63). Uniqueness is enforced by Clerk at create time.
export function slugifyOrgName(name) {
  if (!name) return '';
  return String(name)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 63)
    .replace(/-+$/, '');
}

// The slug the signup will submit: the user's edited value once they've
// touched the field, otherwise the live suggestion derived from the name.
export function getEffectiveOrgSlug(state) {
  if (state.orgSlugTouched) return (state.orgSlug || '').trim();
  return slugifyOrgName(getCreateOrgDisplayName(state));
}

export function buildCreateOrgPayload(state) {
  const isIndividual = state.entityType === ENTITY_TYPE_INDIVIDUAL;
  const displayName = getCreateOrgDisplayName(state);
  const logoSource = getCreateOrgLogoSource(state);
  const isBase64Logo = logoSource.startsWith('data:');

  return {
    orgName: displayName || 'My Organization',
    legalName: isIndividual ? '' : (state.legalName?.trim() || state.orgName?.trim() || ''),
    entityType: state.entityType || 'nonprofit',
    // User profile (sourced from Clerk in OrganizationSetup) so backend can save to account
    firstName: state.firstName || '',
    lastName: state.lastName || '',
    email: state.email || '',
    phoneNumber: state.phoneNumber || '',
    address: {
      line1: '',
      line2: '',
      city: isIndividual ? '' : (state.city || ''),
      state: isIndividual ? '' : (state.state || ''),
      zip: '',
    },
    ein: isIndividual ? '' : (formatEIN(state.ein) || ''),
    timezone: state.timezone || 'America/New_York',
    // Backend reads isTaxExempt to determine Stripe entity_type + structure.
    isTaxExempt: isIndividual ? false : (state.isTaxExempt ?? false),
    hasWebsite: !!state.hasWebsite,
    websiteUrl: state.hasWebsite ? (state.websiteUrl?.trim() || '') : '',
    logo: isBase64Logo ? '' : logoSource, // Base64 uploaded to S3 after org creation
    brandColor: state.brandColor || state.suggestedBrandColor || state.themeColor || DEFAULT_BRAND_COLOR,
    description: state.description || '',
    causeCategory: isIndividual ? '' : (state.causeCategory || ''),
    // Paid-tier INTENT from the marketing site (?plan=BASE|PLUS), captured at the
    // signup landing. The backend (create_org normalize_plan) stores it as
    // onboardingDetails.selectedPlan → flattened to onboardingSelectedPlan, which
    // drives the in-onboarding subscribe step (PE-1634/PE-1643). Omitted (absent
    // key) for a free/no-intent signup so the backend defaults to no paid intent.
    ...(state.flowMode !== 'create-organization' && readPlanIntent()
      ? { selectedPlan: readPlanIntent() }
      : {}),
    // PE-2048: user-visible URL name. Omitted when too short — the backend
    // then lets Clerk auto-generate one; Clerk is the uniqueness authority
    // (duplicate → 400 INVALID_ORG_INPUT surfaced by create-org).
    ...(getEffectiveOrgSlug(state).length >= 3
      ? { organizationSlug: getEffectiveOrgSlug(state) }
      : {}),
  };
}
